import React, { useState, useRef, useEffect } from 'react';
import { MdSearch, MdExpandMore, MdClose, MdAccountBalance } from 'react-icons/md';
import './AccountSelect.css';

function AccountSelect({ accounts, selectedId, onSelect, label = 'Conta', hasError = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filtrar contas por termo de busca
  const filteredAccounts = accounts
    .filter(account => 
      account.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(account => account.is_active !== false);

  const selectedAccount = accounts.find(acc => acc.id === selectedId);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focar no input de busca ao abrir
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (account) => {
    onSelect(account.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect('');
    setSearchTerm('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <div className="account-select" ref={dropdownRef}>
      <label className="account-select-label">{label}</label>
      
      <div 
        className={`account-select-trigger ${isOpen ? 'open' : ''} ${!selectedAccount ? 'empty' : ''} ${hasError ? 'error' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedAccount ? (
          <div className="selected-account">
            <div 
              className="account-color-indicator"
              style={{ backgroundColor: selectedAccount.color || '#10243C' }}
            />
            <div className="account-info">
              <span className="account-name">{selectedAccount.name}</span>
              {selectedAccount.current_balance !== undefined && (
                <span className="account-balance">
                  {formatCurrency(selectedAccount.current_balance)}
                </span>
              )}
            </div>
            {selectedAccount && (
              <button
                type="button"
                className="clear-btn"
                onClick={handleClear}
                title="Limpar seleção"
              >
                <MdClose size={18} />
              </button>
            )}
          </div>
        ) : (
          <span className="placeholder">Selecione uma conta</span>
        )}
        <MdExpandMore className={`expand-icon ${isOpen ? 'rotated' : ''}`} size={24} />
      </div>

      {isOpen && (
        <div className="account-select-dropdown">
          <div className="account-search">
            <MdSearch size={20} className="search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar conta..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="account-options">
            {filteredAccounts.length === 0 ? (
              <div className="no-results">
                <p>Nenhuma conta encontrada</p>
              </div>
            ) : (
              filteredAccounts.map((account) => {
                const isSelected = account.id === selectedId;

                return (
                  <div
                    key={account.id}
                    className={`account-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(account)}
                  >
                    <div 
                      className="account-color-indicator"
                      style={{ backgroundColor: account.color || '#10243C' }}
                    />
                    <div className="account-info">
                      <span className="account-name">{account.name}</span>
                      {account.current_balance !== undefined && (
                        <span className="account-balance">
                          {formatCurrency(account.current_balance)}
                        </span>
                      )}
                    </div>
                    {isSelected && (
                      <span className="check-mark">✓</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountSelect;
